import DetailsCard from 'components/ui/DetailsCard/DetailsCard';
import { appRoutes } from 'constants/navigation';
import {
  ClientDomainType,
  useDeiStageActionCandidateClearAllForCandidateMutation,
  useRequestPasswordRecoveryMutation,
  useUserDeleteMutation,
  useUserUpdateMutation,
} from 'generated/graphql';
import validate from 'helpers/validate';
import { TSelectFormFieldValue } from 'interfaces/forms/form';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  EditFormWrapper,
  NotificationModal,
  NotificationModalType,
  TextInputField,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import useUserStyles from '../../UserStyle';
import { IEditUserInfo, IErrors, IModalStatus } from '../UserDetails.interface';
import ClientSelectModal from './ClientSelectModal/ClientSelectModal';
import styles from './EditUserInfo.module.scss';

const EditUserInfo: FC<IEditUserInfo> = ({ userInfo }) => {
  const { handleMsgType } = useNotification();
  const navigate = useNavigate();
  const classes = useUserStyles();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [formValues, setFormValues] = useState({
    firstName: userInfo?.firstName || '',
    lastName: userInfo?.lastName || '',
    email: userInfo?.email || '',
  });

  const [modalStatus, setModalStatus] = useState<IModalStatus>({
    isOpen: false,
    msg: '',
  });
  const [clientSelectModalStatus, setClientSelectModalStatus] =
    useState<boolean>(false);
  const [isDeiDeleteModalOpen, setIsDeiDeleteModalOpen] =
    useState<boolean>(false);

  const [errors, setErrors] = useState<IErrors>({
    email: '',
    firstName: '',
    lastName: '',
  });

  const handleFormChange = (value: TSelectFormFieldValue, name: string) => {
    setFormValues(props => ({
      ...props,
      [name]: value,
    }));
  };

  const [userUpdateMutation] = useUserUpdateMutation({
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        title: 'User details updated successfully',
        dismissable: true,
      });
      setIsEditMode(false);
    },
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
        dismissable: true,
      });
    },
  });

  const [requestPasswordRecoveryMutation] = useRequestPasswordRecoveryMutation({
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
        dismissable: true,
      });
    },
    onCompleted: () => {
      setClientSelectModalStatus(false);
      handleMsgType({
        type: TNotification.success,
        title: 'Password reset link sent to email',
        dismissable: true,
      });
    },
  });

  const [userDeleteMutation] = useUserDeleteMutation({
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
        dismissable: true,
      });
    },
    onCompleted: () => {
      setIsEditMode(false);
      handleMsgType({
        type: TNotification.success,
        title: 'User deleted successfully',
        dismissable: true,
      });
      closeModal();
      navigate(appRoutes.users.url());
    },
  });

  const handleConfirmDeleteUser = () => {
    if (userInfo) {
      userDeleteMutation({
        variables: { id: Number(userInfo.id) },
      });
    }
  };

  const [clearDeiMutation, { loading: isClearingDei }] =
    useDeiStageActionCandidateClearAllForCandidateMutation({
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
          title: 'Something went wrong',
          dismissable: true,
        });
      },
      onCompleted: data => {
        const count = data.DeiStageActionCandidateClearAllForCandidate;
        handleMsgType({
          type: TNotification.success,
          title:
            count > 0
              ? `Cleared ${count} DE&I submission${count === 1 ? '' : 's'}`
              : 'No DE&I submissions to clear',
          dismissable: true,
        });
        setIsDeiDeleteModalOpen(false);
      },
    });

  const handleConfirmClearDei = () => {
    if (userInfo) {
      clearDeiMutation({
        variables: { userId: Number(userInfo.id) },
      });
    }
  };

  const handleDeleteUser = () => {
    setModalStatus({
      ...modalStatus,
      isOpen: true,
    });
  };

  const handleUpdateUserInfo = () => {
    const inputError = formValidation(formValues as IErrors);
    if (inputError) {
      setErrors(inputError);
    } else if (userInfo?.id && formValues) {
      const variables = {
        ...formValues,
        id: Number(userInfo.id),
      };
      userUpdateMutation({ variables });
    }
  };

  const formValidation = (newValues: IErrors) => {
    return validate(newValues, {
      email: {
        nonEmptyString: true,
        email: '^Not a valid email',
      },
      firstName: {
        nonEmptyString: true,
      },
      lastName: {
        nonEmptyString: true,
      },
    });
  };

  const handlePasswordReminder = (clientDomainType: ClientDomainType) => {
    if (formValues.email) {
      requestPasswordRecoveryMutation({
        variables: {
          email: formValues.email,
          clientDomainType: clientDomainType,
        },
      });
    }
  };

  const closeModal = () => {
    setModalStatus({
      ...modalStatus,
      isOpen: false,
    });
  };

  const detailsItems = [
    [
      {
        label: 'Given Name',
        value: userInfo?.firstName,
      },
      {
        label: 'Id',
        value: `${userInfo?.id}`,
        hasCopyButton: true,
      },
    ],
    [
      {
        label: 'Family Name',
        value: userInfo?.lastName,
      },
      {
        label: 'Sub-ID',
        value: `${userInfo?.subId}`,
        hasCopyButton: true,
      },
    ],
    [
      {
        label: 'Email',
        value: userInfo?.email,
        hasCopyButton: true,
      },
    ],
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          {userInfo?.firstName} {userInfo?.lastName}
        </h1>

        <div className={styles.buttonContainer}>
          {!isEditMode && (
            <Button onClick={() => setIsEditMode(true)}>Edit</Button>
          )}
        </div>
      </div>
      <div>
        {!isEditMode ? (
          <DetailsCard items={detailsItems} />
        ) : (
          <EditFormWrapper
            title="Edit User"
            formId="edit-user-form"
            onSaveClick={handleUpdateUserInfo}
            onCancelClick={() => setIsEditMode(false)}
            hasResetAllButton={false}
          >
            <TextInputField
              id="firstName"
              label="Given Name"
              value={formValues.firstName}
              name="firstName"
              onChange={handleFormChange}
              hasError={!!errors.firstName}
              bottomText={errors.firstName}
            />
            <TextInputField
              id="lastName"
              label="Family Name"
              value={formValues.lastName}
              name="lastName"
              onChange={handleFormChange}
              hasError={!!errors.lastName}
              bottomText={errors.lastName}
            />
            <TextInputField
              id="email"
              label="Email"
              value={formValues.email}
              name="email"
              onChange={handleFormChange}
              hasError={!!errors.email}
              bottomText={errors.email}
            />
          </EditFormWrapper>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          margin: '20px 0',
        }}
      >
        <div className={styles.buttonContainer}>
          <Button
            variant="secondary"
            onClick={() => {
              setClientSelectModalStatus(true);
            }}
          >
            Send Password Reminder
          </Button>
          <Button
            variant="negativeSecondary"
            onClick={() => setIsDeiDeleteModalOpen(true)}
          >
            Delete all DE&I data
          </Button>
          <Button variant="negativeSecondary" onClick={handleDeleteUser}>
            Delete user
          </Button>
        </div>
        <div className={classes.mr10}></div>
      </div>
      {modalStatus.isOpen && (
        <NotificationModal
          isOpen={modalStatus.isOpen}
          title="Are you sure?"
          message={`You are about to delete ${formValues.firstName} ${formValues.lastName}'s account. This action is permanent.`}
          onClose={closeModal}
          onOkClick={handleConfirmDeleteUser}
          okButtonChildren="Delete user"
          confirmButtonProps={{
            variant: 'negativePrimary',
          }}
          type={NotificationModalType.WARNING}
        />
      )}
      {isDeiDeleteModalOpen && (
        <NotificationModal
          isOpen={isDeiDeleteModalOpen}
          title="Are you sure?"
          message={`You are about to permanently erase all demographic information questionnaire answers ${formValues.firstName} ${formValues.lastName} submitted, across every assessment. This action cannot be undone.`}
          onClose={() => setIsDeiDeleteModalOpen(false)}
          onOkClick={handleConfirmClearDei}
          okButtonChildren="Delete all DE&I data"
          confirmButtonProps={{
            variant: 'negativePrimary',
            loading: isClearingDei,
          }}
          type={NotificationModalType.WARNING}
        />
      )}
      <ClientSelectModal
        open={clientSelectModalStatus}
        message={`Please select the client domain type.`}
        onSave={handlePasswordReminder}
        onClose={() => setClientSelectModalStatus(false)}
      />
    </div>
  );
};

export default EditUserInfo;
